import { useState, useContext, useEffect, useMemo } from 'react';
import {
  Box,
  Text,
  VStack,
  Button,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  Flex,
  Divider,
  Link,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { LockIcon, WarningIcon, ArrowBackIcon } from '@chakra-ui/icons';
import {
  useStripe,
  useElements,
  AddressElement,
  PaymentElement,
} from '@stripe/react-stripe-js';
import { HttpContext } from '../../../context/HttpContext';
import { MeContext } from '../../../context/MeContext';
import CustomToast from '../../../common/CustomToast';
import TaxIdSelect from '../../common/TaxIdSelect';
import RequestCustomPlanModal from './RequestCustomPlanModal';
import PlanSelectionGrid from './PlanSelectionGrid';
import StripeElementsProvider from './StripeElementsProvider';
import { STRIPE_APPEARANCE } from './stripeAppearance';
import { useBilling } from '../../../store/client/BillingContext';
import {
  customOfferBilledIntervalPhrase,
  formatGbpFromMinorUnits,
  formatCustomPlanName,
} from '../../../utils/subscriptionUtils';

const BILLING_INTERVAL_SUFFIX = {
  annually: 'yr',
  quarterly: 'qtr',
  semiannually: '6mo',
  monthly: 'mo',
};

const resolveCustomPlanPricing = (customPlanOffer, customPlanInterval) => {
  const annualOption =
    customPlanOffer?.annualOption ?? customPlanOffer?.annual_option;
  if (annualOption && customPlanInterval) {
    return {
      amount:
        customPlanInterval === 'annually'
          ? annualOption.annualAmountPence
          : annualOption.monthlyAmountPence,
      interval: customPlanInterval,
    };
  }
  return {
    amount: customPlanOffer?.offeredPlan?.amount,
    interval: customPlanOffer?.billingInterval,
  };
};

const CardlessTrialConversionModal = ({
  isOpen,
  onClose,
  customPlanOffer,
  customPlanInterval,
}) => {
  const { authAxios } = useContext(HttpContext);
  const meCtx = useContext(MeContext);

  const { account } = meCtx.state;
  const { customPlanRequest } = useBilling();
  const companyName = account?.company_name;
  const accountEmail = account?.email;

  const requestCustomPlanModal = useDisclosure();

  const [currentStep, setCurrentStep] = useState(1);
  const [subscriptionOptions, setSubscriptionOptions] = useState([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedInterval, setSelectedInterval] = useState('year');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (isOpen && customPlanOffer) {
      setCurrentStep(2);
    }
  }, [isOpen, customPlanOffer]);

  const staticPlans = useMemo(
    () => [
      {
        id: 'custom',
        name: 'Custom',
        buttonText: 'Request a plan',
      },
      {
        id: 'enterprise',
        name: 'Enterprise',
        buttonText: 'Contact sales',
      },
    ],
    []
  );

  const handleBookDemo = () => {
    const name = account ? `${account.first_name} ${account.last_name}` : '';
    const email = account?.email || '';
    const url = `https://calendly.com/passentrydemo/30min?name=${name}&email=${email}`;
    if (window.Calendly && window.Calendly.initPopupWidget) {
      window.Calendly.initPopupWidget({ url });
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  useEffect(() => {
    const fetchAvailablePlans = async () => {
      if (!isOpen || customPlanOffer) return;

      setIsLoadingPlans(true);
      try {
        const allPlans = await authAxios.get(
          '/internal/v1/billing/subscription_plans'
        );
        setSubscriptionOptions(allPlans.data);
      } catch (error) {
        console.error('Error fetching available plans:', error);
        setErrorMessage('Failed to load subscription plans. Please try again.');
      } finally {
        setIsLoadingPlans(false);
      }
    };

    fetchAvailablePlans();
  }, [authAxios, isOpen]);

  const handleBack = () => {
    if (customPlanOffer) {
      setErrorMessage('');
      onClose();
      return;
    }
    setCurrentStep(1);
    setErrorMessage('');
  };

  const handleClose = () => {
    setCurrentStep(1);
    setSelectedPlan(null);
    setErrorMessage('');
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size={currentStep === 1 ? '5xl' : '6xl'}
    >
      <ModalOverlay />
      <ModalContent
        pt={currentStep === 1 ? 6 : 3}
        pb={currentStep === 1 ? 8 : 4}
        px={4}
      >
        <ModalCloseButton />
        <ModalBody>
          {currentStep === 1 ? (
            <PlanSelectionGrid
              plans={subscriptionOptions}
              isLoading={isLoadingPlans}
              selectedInterval={selectedInterval}
              onIntervalChange={(interval) => setSelectedInterval(interval)}
              onPlanSelect={(option, price) => {
                setSelectedPlan({
                  id: option.id,
                  name: option.name,
                  selectedPrice: price,
                });
                setCurrentStep(2);
              }}
              title="Select a plan"
              staticPlans={staticPlans}
              customPlanRequest={customPlanRequest}
              onOpenRequestCustomPlan={requestCustomPlanModal.onOpen}
              handleBookDemo={handleBookDemo}
              errorMessage={errorMessage}
            />
          ) : (
            <BillingStep
              selectedPlan={selectedPlan}
              customPlanOffer={customPlanOffer}
              customPlanInterval={customPlanInterval}
              companyName={companyName}
              accountEmail={accountEmail}
              onBack={handleBack}
              onSuccess={handleClose}
            />
          )}
        </ModalBody>
      </ModalContent>

      <RequestCustomPlanModal
        isOpen={requestCustomPlanModal.isOpen}
        onClose={requestCustomPlanModal.onClose}
        onSuccess={handleClose}
      />
    </Modal>
  );
};

const BillingStep = (props) => {
  const { selectedPlan, customPlanOffer, customPlanInterval } = props;

  const planAmount = customPlanOffer
    ? resolveCustomPlanPricing(customPlanOffer, customPlanInterval).amount
    : selectedPlan?.selectedPrice?.amount;
  const planCurrency =
    (customPlanOffer
      ? customPlanOffer.offeredPlan?.currency
      : selectedPlan?.selectedPrice?.currency) || 'gbp';

  return (
    <StripeElementsProvider
      mode="subscription"
      amount={planAmount}
      currency={planCurrency}
      paymentMethodTypes={['card']}
      appearance={STRIPE_APPEARANCE}
    >
      <BillingStepFields {...props} />
    </StripeElementsProvider>
  );
};

const BillingStepFields = ({
  selectedPlan,
  customPlanOffer,
  customPlanInterval,
  companyName,
  accountEmail,
  onBack,
  onSuccess,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { amount: customPlanAmount, interval: customPlanBillingInterval } =
    resolveCustomPlanPricing(customPlanOffer, customPlanInterval);
  const { authAxios } = useContext(HttpContext);
  const meCtx = useContext(MeContext);
  const { fetchData: refreshMeContext } = meCtx;
  const { refresh: refreshBilling } = useBilling();
  const toast = useToast();

  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingSubscription, setIsCreatingSubscription] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  const [taxIdData, setTaxIdData] = useState({ type: '', value: '' });
  const [taxIdValidation, setTaxIdValidation] = useState({
    isValid: null,
    message: '',
    hasBlockingErrors: false,
  });

  const [elementStates, setElementStates] = useState({
    address: { complete: false, empty: true },
    paymentElement: { complete: false, empty: true },
  });

  const handleTaxIdChange = (newTaxIdData) => setTaxIdData(newTaxIdData);
  const handleTaxIdValidationChange = (validationResult) =>
    setTaxIdValidation(validationResult);

  const handleElementChange = (elementType) => (event) => {
    setElementStates((prev) => ({
      ...prev,
      [elementType]: {
        complete: event.complete,
        empty: event.empty,
        error: event.error,
      },
    }));
  };

  const formatPrice = (amount) =>
    formatGbpFromMinorUnits(amount, { wholePounds: true });

  const isFormValid = () => {
    const isTaxIdIncomplete = taxIdData.type && !taxIdData.value;
    return (
      stripe &&
      !isLoading &&
      elementStates.address.complete &&
      elementStates.paymentElement.complete &&
      !isTaxIdIncomplete
    );
  };

  const hasValidationErrors = () => {
    if (taxIdData.value && taxIdValidation.isValid === false) return true;
    return taxIdValidation.hasBlockingErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    setErrorMessage('');

    try {
      if (
        taxIdData.value &&
        taxIdValidation.isValid === false &&
        taxIdValidation.hasBlockingErrors
      ) {
        throw new Error(
          'Please enter a valid tax ID format. This tax ID will be verified against government databases.'
        );
      }

      if (!stripe || !elements) {
        throw new Error('Stripe has not been initialized.');
      }

      const { error: submitError } = await elements.submit();
      if (submitError) {
        throw new Error(submitError.message);
      }

      const addressElement = elements.getElement(AddressElement);
      const { value: addressDetails } = await addressElement.getValue();

      const billingDetails = {
        name: addressDetails.name,
        email: accountEmail,
        phone: addressDetails.phone,
        address: addressDetails.address,
      };

      const prepareUrl = customPlanOffer
        ? `/internal/v1/billing/custom-plan-requests/${customPlanOffer.id}/prepare_subscribe`
        : `/internal/v1/billing/subscriptions/prepare_subscribe`;
      const confirmUrl = customPlanOffer
        ? `/internal/v1/billing/custom-plan-requests/${customPlanOffer.id}/confirm_subscribe`
        : `/internal/v1/billing/subscriptions/confirm_subscribe`;

      setIsCreatingSubscription(true);

      let prepareResponse;
      try {
        prepareResponse = await authAxios.post(prepareUrl, {
          ...(!customPlanOffer && {
            plan_id: selectedPlan.id,
            price_id: selectedPlan.selectedPrice.id,
          }),
          ...(customPlanOffer &&
            customPlanInterval && { billing_interval: customPlanInterval }),
          billing_details: billingDetails,
          ...(taxIdData.type &&
            taxIdData.value && {
              tax_id: taxIdData.value,
              tax_id_type: taxIdData.type,
            }),
        });
      } catch (apiError) {
        const errorMsg =
          apiError.response?.data?.error ||
          apiError.response?.data?.errors?.[0]?.error ||
          apiError.response?.data?.errors?.[0] ||
          'Failed to prepare subscription. Please try again.';
        throw new Error(errorMsg);
      }

      const {
        client_secret: invoiceClientSecret,
        stripe_subscription_id: stripeSubscriptionId,
      } = prepareResponse.data;

      setIsCreatingSubscription(false);
      setIsAuthenticating(true);

      const { error: confirmError } = await stripe.confirmPayment({
        elements,
        clientSecret: invoiceClientSecret,
        confirmParams: {
          return_url: window.location.href,
          payment_method_data: { billing_details: billingDetails },
        },
        redirect: 'if_required',
      });

      setIsAuthenticating(false);
      setIsCreatingSubscription(true);

      try {
        await authAxios.post(confirmUrl, {
          success: !confirmError,
          stripe_subscription_id: stripeSubscriptionId,
          error: confirmError
            ? {
                message: confirmError.message,
                decline_code: confirmError.decline_code,
                code: confirmError.code,
              }
            : null,
        });
      } catch (confirmApiError) {
        if (confirmError) {
          throw new Error(confirmError.message);
        }
        const errorMsg =
          confirmApiError.response?.data?.error ||
          confirmApiError.response?.data?.errors?.[0]?.error ||
          confirmApiError.response?.data?.errors?.[0] ||
          'Failed to confirm subscription. Please try again.';
        throw new Error(errorMsg);
      }

      if (confirmError) {
        throw new Error(
          confirmError.message ||
            'Card authentication failed. Please try again.'
        );
      }

      toast({
        render: (props) => (
          <CustomToast
            status="success"
            title="Success"
            description={
              customPlanOffer
                ? 'Your custom plan is now active'
                : 'Your subscription is now active'
            }
            onClose={props.onClose}
            id={props.id}
          />
        ),
      });

      refreshMeContext();
      await refreshBilling();
      onSuccess();
    } catch (err) {
      const errorMsg =
        err.response?.data?.error ||
        err.response?.data?.errors?.[0]?.error ||
        err.response?.data?.errors?.[0] ||
        err.message ||
        'Something went wrong. Please try again.';

      setErrorMessage(errorMsg);
    } finally {
      setIsLoading(false);
      setIsCreatingSubscription(false);
      setIsAuthenticating(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Flex align="center" mb={1}>
        <Button
          variant="ghost"
          leftIcon={<ArrowBackIcon />}
          onClick={onBack}
          size="sm"
          mr={4}
        >
          Back
        </Button>
      </Flex>

      <Text textStyle="heading3" mb={2}>
        Start your subscription
      </Text>

      <Flex
        direction={{ base: 'column', md: 'row' }}
        gap={6}
        w="100%"
        align="flex-start"
      >
        <VStack alignItems="flex-start" flex={3} spacing={4}>
          <Box w="100%">
            <Text
              textStyle="headingFamilySemiBold"
              fontSize="16px"
              color="#101828"
            >
              Company details
            </Text>
            <Text fontSize="12px" color="orangePrimary" ml={1}>
              All fields required
            </Text>
            <FormControl mt={3} mb={0}>
              <Box mb={0}>
                <AddressElement
                  options={{
                    mode: 'billing',
                    fields: { phone: 'always' },
                    validation: { phone: { required: 'always' } },
                    display: { name: 'organization' },
                    defaultValues: {
                      name: companyName || '',
                    },
                  }}
                  onChange={handleElementChange('address')}
                />
              </Box>
              {elementStates.address.error && (
                <Text color="red.500" fontSize="sm" mt={2} ml={2}>
                  {elementStates.address.error.message}
                </Text>
              )}
            </FormControl>
            <Flex direction="row" gap={1} w="100%" mt={2} align="center">
              <WarningIcon color="#989898" w="12px" h="12px" />
              <Text
                color="#989898"
                fontSize="12px"
                textStyle="headingFamilyMedium"
                mt={0.5}
              >
                VAT is calculated based on your billing contact address.
              </Text>
            </Flex>
          </Box>

          <Box w="100%">
            <Text
              textStyle="headingFamilySemiBold"
              fontSize="16px"
              color="#101828"
            >
              Payment details
            </Text>

            <FormControl mt={3} isRequired>
              <FormLabel color="#4C4C4C" fontSize="14px">
                Card or payment method
              </FormLabel>
              <PaymentElement
                options={{ fields: { billingDetails: 'never' } }}
                onChange={handleElementChange('paymentElement')}
              />
              {elementStates.paymentElement.error && (
                <Text color="red.500" fontSize="sm" mt={1}>
                  {elementStates.paymentElement.error.message}
                </Text>
              )}
            </FormControl>

            <Flex direction="row" gap={1} w="100%" align="center" mt={2}>
              <LockIcon color="#989898" w="12px" h="12px" />
              <Text
                color="#989898"
                fontSize="12px"
                textStyle="headingFamilyMedium"
                mt={1}
              >
                Secured with SSL
              </Text>
            </Flex>
          </Box>

          <Box w="100%">
            <Text
              textStyle="headingFamilySemiBold"
              fontSize="16px"
              color="#101828"
            >
              Tax ID (optional)
            </Text>
            <TaxIdSelect
              type={taxIdData.type}
              value={taxIdData.value}
              onChange={handleTaxIdChange}
              onValidationChange={handleTaxIdValidationChange}
              mt={2}
            />
          </Box>
        </VStack>

        <VStack
          alignItems="flex-start"
          flex={2}
          spacing={3}
          alignSelf="flex-start"
        >
          <Box
            borderRadius="10px"
            py={8}
            px={6}
            w="100%"
            bgGradient="linear(180deg, #FF9554 16%, #FF4935 100%)"
          >
            <Flex justifyContent="space-between" alignItems="center">
              <Box>
                <Text
                  textStyle="headingFamilyMedium"
                  fontSize="16px"
                  color="white"
                >
                  {customPlanOffer
                    ? formatCustomPlanName(customPlanOffer.offeredPlan.name)
                    : selectedPlan?.name}{' '}
                </Text>
                <Text textStyle="bodySmall" color="whiteAlpha.800">
                  Billed{' '}
                  {customPlanOffer
                    ? customOfferBilledIntervalPhrase(customPlanBillingInterval)
                    : selectedPlan?.selectedPrice?.interval === 'year'
                      ? 'annually'
                      : 'monthly'}
                </Text>
              </Box>
              <Flex alignItems="baseline" gap={1}>
                <Text
                  textStyle="headingFamilySemiBold"
                  fontSize="22px"
                  color="white"
                >
                  {formatPrice(
                    customPlanOffer
                      ? customPlanAmount
                      : selectedPlan?.selectedPrice?.amount
                  )}
                </Text>
                <Text textStyle="bodySmall" color="whiteAlpha.800">
                  /
                  {customPlanOffer
                    ? BILLING_INTERVAL_SUFFIX[customPlanBillingInterval] || 'mo'
                    : selectedPlan?.selectedPrice?.interval === 'year'
                      ? 'yr'
                      : 'mo'}
                </Text>
              </Flex>
            </Flex>
          </Box>

          <Box
            position="relative"
            borderRadius="10px"
            py={4}
            px={4}
            bg="white"
            w="100%"
            _before={{
              content: '""',
              position: 'absolute',
              inset: 0,
              padding: '2px',
              borderRadius: 'inherit',
              background: 'linear-gradient(180deg, #FF9554 16%, #FF4935 100%)',
              WebkitMask:
                'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
              WebkitMaskComposite: 'xor',
              maskComposite: 'exclude',
              pointerEvents: 'none',
            }}
          >
            <Text
              textStyle="headingFamilyMedium"
              fontSize="16px"
              color="#101828"
              mb={1}
            >
              Summary
            </Text>
            <Text fontSize="14px" color="#4C4C4C">
              Your subscription will start immediately.
            </Text>

            <Flex
              direction="row"
              w="100%"
              justifyContent="space-between"
              mt={3}
            >
              <Text fontSize="14px" color="#4C4C4C">
                Total due today (excl. tax)
              </Text>
              <Text
                fontSize="18px"
                textStyle="headingFamilyBold"
                color="#FF4935"
              >
                {formatPrice(
                  customPlanOffer
                    ? customPlanAmount
                    : selectedPlan?.selectedPrice?.amount
                )}
              </Text>
            </Flex>

            <Divider
              my={2}
              borderColor="transparent"
              height="1px"
              background="linear-gradient(180deg, #FF9554 16%, #FF4935 100%)"
            />

            <Flex direction="row" w="100%" justifyContent="space-between">
              <Text fontSize="14px" color="#4C4C4C">
                Renewal price
              </Text>
              <Text fontSize="14px" color="#4C4C4C">
                {formatPrice(
                  customPlanOffer
                    ? customPlanAmount
                    : selectedPlan?.selectedPrice?.amount
                )}
                /
                {customPlanOffer
                  ? customOfferBilledIntervalPhrase(customPlanBillingInterval)
                  : selectedPlan?.selectedPrice?.interval === 'year'
                    ? 'year'
                    : 'month'}
              </Text>
            </Flex>

            {errorMessage && (
              <Alert status="error" borderRadius="md" mt={3}>
                <AlertIcon />
                {errorMessage}
              </Alert>
            )}

            <Button
              type="submit"
              variant="gradient"
              w="100%"
              mt={4}
              isLoading={isLoading}
              loadingText={
                isAuthenticating
                  ? 'Authenticating Card...'
                  : isCreatingSubscription
                    ? 'Activating Subscription...'
                    : 'Processing...'
              }
              isDisabled={
                !stripe || isLoading || hasValidationErrors() || !isFormValid()
              }
            >
              Start subscription
            </Button>

            {!isFormValid() && !isLoading && (
              <Text fontSize="sm" color="gray.500" textAlign="center" mt={2}>
                {taxIdData.type && !taxIdData.value
                  ? 'Please enter a VAT/Tax ID or select "None" to continue'
                  : 'Please complete all required fields to continue'}
              </Text>
            )}
          </Box>

          {hasValidationErrors() && (
            <Text fontSize="sm" color="red.500" textAlign="center" w="100%">
              Please correct the tax ID format before continuing.
            </Text>
          )}

          <Flex justifyContent="center" w="100%">
            <Text fontSize="12px" color="#252525" textAlign="center">
              By starting your subscription, you agree to the{' '}
              <Link
                href="https://passentry.com/terms-of-service"
                textDecoration="underline"
                isExternal
              >
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link
                href="https://passentry.com/privacy-policy"
                textDecoration="underline"
                isExternal
              >
                Privacy Policy
              </Link>
            </Text>
          </Flex>
        </VStack>
      </Flex>
    </form>
  );
};

export default CardlessTrialConversionModal;
